import { isAfter, isEqual, isSameDay, parse } from 'date-fns';
import { MembershipFee } from '../model/membership-fee';
import { PaymentMoment } from '../model/payment-moment';
import { UnemploymentPaymentRequest } from '../model/unemployment-payment-request';
import { ApplicationResponse } from '../model/application-response';
import {
  isIncompleteStatus,
  extractMissingDocuments as extractMissingDocumentsUtil
} from '../services/unemployment-application.service';

export function isoToDate(s?: string): Date | null {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(+d) ? null : d;
}
export function ymToDate(ym?: string): Date | null {
  if (!ym) return null;
  const s = String(ym).replace('-', '');
  if (!/^\d{6}$/.test(s)) return null;
  const y = +s.slice(0, 4);
  const m = +s.slice(4, 6) - 1;
  return new Date(y, m, 1);
}
function toNum(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

// ---------- MembershipFee ----------
export class MembershipFeeVM {
  readonly raw: MembershipFee;
  readonly dateOfPaymentDate: Date | null;
  readonly contributionPeriodFromMonthDate: Date | null;
  readonly contributionPeriodToMonthDate: Date | null;

  constructor(raw: MembershipFee) {
    this.raw = raw;
    this.dateOfPaymentDate = isoToDate(raw?.dateOfPayment);
    this.contributionPeriodFromMonthDate = ymToDate(raw?.contributionPeriodFromMonth);
    this.contributionPeriodToMonthDate = ymToDate(raw?.contributionPeriodToMonth);
  }
}

// ---------- PaymentMoment ----------
export class PaymentMomentVM {
  readonly raw: PaymentMoment;
  readonly dateOfPaymentDate: Date | null;
  readonly unemploymentMonthDate: Date | null;

  constructor(raw: PaymentMoment) {
    this.raw = raw;
    this.dateOfPaymentDate = isoToDate(raw?.dateOfPayment);
    this.unemploymentMonthDate = ymToDate(raw?.unemploymentMonth);
  }

  sumUnemploymentGrossAmount(): number {
    return (this.raw?.paymentCalculations ?? [])
      .reduce((acc, c) => acc + toNum(c?.unemploymentInfo?.unemploymentGrossAmount), 0);
  }
  sumUnemploymentDays(): number {
    return (this.raw?.paymentCalculations ?? [])
      .reduce((acc, c) => acc + toNum(c?.unemploymentInfo?.unemploymentDays), 0);
  }
  sumAdditionalCompensationGrossAmount(): number {
    return (this.raw?.paymentCalculations ?? [])
      .reduce((acc, c) => acc + toNum(c?.additionalCompensation?.additionalCompensationGrossAmount), 0);
  }
  sumAdditionalCompensationDays(): number {
    return (this.raw?.paymentCalculations ?? [])
      .reduce((acc, c) => acc + toNum(c?.additionalCompensation?.additionalCompensationDays), 0);
  }
}

// ---------- Requests / Applications ----------
export class UnemploymentPaymentRequestVM {
  readonly raw: UnemploymentPaymentRequest;
  readonly requestDateDate: Date | null;

  constructor(raw: UnemploymentPaymentRequest) {
    this.raw = raw;
    this.requestDateDate = isoToDate(raw?.requestDate);
  }

  isIncomplete(): boolean {
    return isIncompleteStatus(this.raw?.requestState);
  }
}

export class ApplicationResponseVM {
  readonly raw: ApplicationResponse;
  readonly requestDateDate: Date | null;

  constructor(raw: ApplicationResponse) {
    this.raw = raw;
    this.requestDateDate = isoToDate(raw?.requestDate);
  }

  matchesCutoffAndSameDayAs(req: UnemploymentPaymentRequestVM, cutoff: string = '2025-05-01'): boolean {
    if (!this.requestDateDate || !req.requestDateDate) return false;
    const appDate = this.requestDateDate;
    const cutoffDate = parse(cutoff, 'yyyy-MM-dd', new Date());
    const expectedDate = req.requestDateDate;
    const isSameOrAfterCutoff = isAfter(appDate, cutoffDate) || isEqual(appDate, cutoffDate);
    return isSameOrAfterCutoff && isSameDay(appDate, expectedDate);
  }

  extractMissingDocuments(): string {
    return extractMissingDocumentsUtil(this.raw);
  }
}
